import { animate, AnimatePresence, motion, useMotionValue } from "framer-motion";
import { PanelLeft, PanelLeftClose } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import Navigation from "./Navigation";
import ObservabilitySecondarySidebarDrawer from "./SecondarySidebar/ObservabilitySecondarySidebarDrawer";
import {
  CLOSE_REVIEW_LOGS_DRAWER_EVENT,
  SCROLL_REVIEW_LOGS_DRAWER_TO_TOP_EVENT,
} from "./SecondarySidebar/SearchBar/ReviewLogsDrawerControls/constants";
import { SettingsNavigation } from "./SettingsNavigation";
import { SidebarProps, SidebarView } from "./types";
import UserSection from "./UserSection";
import { useReviewLogsDrawerIntentParams } from "./hooks";

import logoXmas from "@/assets/logo/logo_xmas.png";
import logoCakeColor from "@/assets/logo/promptlayer-cake-color.png";
import logoCakeColorXmas from "@/assets/logo/promptlayer-cake-color_xmas.svg";
import logoLongColor from "@/assets/logo/promptlayer-long-color.png";
import {
  OPEN_TRACES_DRAWER_EVENT,
  useSidebarTab,
} from "@/components/SidebarSearchLayout/sidebar-tab-context";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { SESSION_STORAGE } from "@/constants";
import { useLocalStorageState } from "@/hooks";
import { LOCAL_STORAGE_KEYS } from "@/hooks/useLocalStorageState";
import useSessionStorageState, { SESSION_STORAGE_KEYS } from "@/hooks/useSessionStorageState";
import {
  REQUEST_LOGS_DRAWER_INTENT,
  withPreservedWorkspaceSearch,
} from "@/utils/requestLogUrlParams";
import { TRACES_DRAWER_INTENT } from "@/utils/tracesUrlParams";
import {
  isWorkspaceAnalyticsPathname,
  isWorkspaceRequestLogsTablePathname,
  isWorkspaceTracesAnalyticsPathname,
  isWorkspaceTracesSearchPathname,
} from "@/utils/workspacePathMatchers";

const SLIDE_TRANSITION = { duration: 0.25, ease: [0.4, 0, 0.2, 1] };
const DEFAULT_SIDEBAR_WIDTH = 320;

const setSidebarElementWidth = (width: number) => {
  const sidebarWrapper = document.querySelector('[data-slot="sidebar-wrapper"]') as HTMLElement;
  const sidebarContainer = document.querySelector('[data-slot="sidebar-container"]') as HTMLElement;

  if (sidebarWrapper) {
    sidebarWrapper.style.setProperty("--sidebar-width", `${width}px`);
  }
  if (sidebarContainer) {
    sidebarContainer.style.width = `${width}px`;
  }
};

const AppSidebar = ({ onRefresh }: SidebarProps & { onRefresh?: () => void }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const logoRotation = useMotionValue(0);
  const logoRotationAnimation = useRef<ReturnType<typeof animate> | null>(null);
  const { workspaceId } = useParams();
  const location = useLocation();
  const sidebarTabContext = useSidebarTab();
  const [searchParams, setSearchParams] = useSearchParams();
  const setActiveSidebarTab = sidebarTabContext?.setActiveTab;

  const isWorkspaceAnalyticsPage = isWorkspaceAnalyticsPathname(location.pathname);
  const isRequestsAnalyticsSurface =
    isWorkspaceAnalyticsPage || isWorkspaceRequestLogsTablePathname(location.pathname);
  const isTracesSurface =
    isWorkspaceTracesSearchPathname(location.pathname) ||
    isWorkspaceTracesAnalyticsPathname(location.pathname);
  const requestsTabActive = sidebarTabContext?.activeTab === "Requests";
  const tracesTabActive = sidebarTabContext?.activeTab === "Traces";
  const hasReviewLogsDrawerIntent = searchParams.get(REQUEST_LOGS_DRAWER_INTENT) === "1";
  const hasTracesDrawerIntent = searchParams.get(TRACES_DRAWER_INTENT) === "1";
  const [showReviewLogsDrawerOnAnalytics, setShowReviewLogsDrawerOnAnalytics] = useState(false);
  const hideSecondaryRequestsPanel = isRequestsAnalyticsSurface && requestsTabActive;

  const isRequestDetailRoute =
    /^\/workspace\/[^/]+\/request\/[^/]+/.test(location.pathname) &&
    !location.pathname.includes("/playground");
  const isTraceDetailRoute = /^\/workspace\/[^/]+\/traces\/[^/]+/.test(location.pathname);

  const navigate = useNavigate();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const [isMobile, setIsMobile] = useState(false);
  const [isSecondarySidebarOpen, setIsSecondarySidebarOpen] = useLocalStorageState(
    LOCAL_STORAGE_KEYS.SIDEBAR_SECONDARY_OPEN,
    false,
  );
  const [sidebarView, setSidebarView] = useSessionStorageState<SidebarView>(
    SESSION_STORAGE_KEYS.SIDEBAR_VIEW,
    "navigation",
  );
  const [savedSidebarWidth, setSavedSidebarWidth] = useLocalStorageState<number>(
    LOCAL_STORAGE_KEYS.SIDEBAR_WIDTH,
    DEFAULT_SIDEBAR_WIDTH,
  );

  const sidebarViewRef = useRef(sidebarView);
  useEffect(() => {
    sidebarViewRef.current = sidebarView;
  }, [sidebarView]);

  const skipAutoDetectRef = useRef(false);

  const isDecember = new Date().getMonth() === 11;
  const activeLogo = isDecember ? logoXmas : logoLongColor;
  const activeLogoCake = isDecember ? logoCakeColorXmas : logoCakeColor;

  const startLogoRotation = () => {
    logoRotationAnimation.current?.stop();
    const currentRotation = logoRotation.get();
    logoRotationAnimation.current = animate(logoRotation, currentRotation + 360, {
      duration: 1.15,
      ease: "linear",
      repeat: Infinity,
    });
  };

  const finishLogoRotation = () => {
    logoRotationAnimation.current?.stop();
    const currentRotation = logoRotation.get();
    const restingRotation = Math.ceil(currentRotation / 360) * 360;
    const remainingRotation = restingRotation - currentRotation;

    logoRotationAnimation.current = animate(logoRotation, restingRotation, {
      duration: Math.max(0.18, (remainingRotation / 360) * 0.55),
      ease: [0.22, 1, 0.36, 1],
      onComplete: () => logoRotation.set(0),
    });
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleOpenTracesDrawer = () => {
      setActiveSidebarTab?.("Traces");
      setIsSecondarySidebarOpen(true);
    };

    window.addEventListener(OPEN_TRACES_DRAWER_EVENT, handleOpenTracesDrawer);

    return () => window.removeEventListener(OPEN_TRACES_DRAWER_EVENT, handleOpenTracesDrawer);
  }, [setIsSecondarySidebarOpen, setActiveSidebarTab]);

  useEffect(() => {
    const handleCloseReviewLogsDrawer = () => {
      setShowReviewLogsDrawerOnAnalytics(false);
      setIsSecondarySidebarOpen(false);
    };

    window.addEventListener(CLOSE_REVIEW_LOGS_DRAWER_EVENT, handleCloseReviewLogsDrawer);
    return () =>
      window.removeEventListener(CLOSE_REVIEW_LOGS_DRAWER_EVENT, handleCloseReviewLogsDrawer);
  }, [setIsSecondarySidebarOpen]);

  useEffect(() => {
    if (isMobile && !isCollapsed) {
      document.querySelector<HTMLButtonElement>("[data-sidebar-trigger]")?.click();
    }
  }, [isMobile, isCollapsed]);

  useEffect(() => {
    const isSettingsPath = location.pathname.includes("/settings");

    if (skipAutoDetectRef.current) {
      if (!isSettingsPath) {
        skipAutoDetectRef.current = false;
      }
      return;
    }

    const currentView = sidebarViewRef.current;

    if (isSettingsPath && currentView === "navigation") {
      setSidebarView("settings");
    } else if (!isSettingsPath && currentView === "settings") {
      setSidebarView("navigation");
    }
  }, [location.pathname, setSidebarView]);

  useEffect(() => {
    if (!isCollapsed && savedSidebarWidth) {
      requestAnimationFrame(() => setSidebarElementWidth(savedSidebarWidth));
    } else if (isCollapsed) {
      const sidebarContainer = document.querySelector(
        '[data-slot="sidebar-container"]',
      ) as HTMLElement;
      if (sidebarContainer) {
        sidebarContainer.style.width = "";
      }
    }
  }, [isCollapsed, savedSidebarWidth]);

  useEffect(() => {
    if (hideSecondaryRequestsPanel) {
      setShowReviewLogsDrawerOnAnalytics(false);
      setIsSecondarySidebarOpen(false);
    }
  }, [
    hasReviewLogsDrawerIntent,
    hideSecondaryRequestsPanel,
    isSecondarySidebarOpen,
    isWorkspaceAnalyticsPage,
    isRequestsAnalyticsSurface,
    location.pathname,
    requestsTabActive,
    setIsSecondarySidebarOpen,
    showReviewLogsDrawerOnAnalytics,
  ]);

  useEffect(() => {
    if (!isRequestsAnalyticsSurface) {
      setShowReviewLogsDrawerOnAnalytics(false);
      return;
    }

    setShowReviewLogsDrawerOnAnalytics(false);
    if (requestsTabActive) {
      setIsSecondarySidebarOpen(false);
    }
  }, [isRequestsAnalyticsSurface, requestsTabActive, setIsSecondarySidebarOpen]);

  const wasTracesSurfaceRef = useRef(false);
  useEffect(() => {
    if (isTracesSurface && !wasTracesSurfaceRef.current) {
      setActiveSidebarTab?.("Traces");
      setIsSecondarySidebarOpen(false);
    }
    wasTracesSurfaceRef.current = isTracesSurface;
  }, [isTracesSurface, setActiveSidebarTab, setIsSecondarySidebarOpen]);

  useEffect(() => {
    if (!isRequestDetailRoute) {
      return;
    }

    setIsSecondarySidebarOpen(true);
    setActiveSidebarTab?.("Requests");
  }, [isRequestDetailRoute, location.pathname, setIsSecondarySidebarOpen, setActiveSidebarTab]);

  useEffect(() => {
    if (!isTraceDetailRoute) {
      return;
    }

    setIsSecondarySidebarOpen(true);
    setActiveSidebarTab?.("Traces");
  }, [isTraceDetailRoute, location.pathname, setIsSecondarySidebarOpen, setActiveSidebarTab]);

  useReviewLogsDrawerIntentParams({
    isWorkspaceAnalyticsPage: isRequestsAnalyticsSurface,
    shouldOpenDrawer: !isRequestsAnalyticsSurface,
    searchParams,
    setSearchParams,
    setIsSecondarySidebarOpen,
    setActiveSidebarTab,
    setShowReviewLogsDrawerOnAnalytics,
  });

  useEffect(() => {
    if (!hasTracesDrawerIntent) {
      return;
    }

    setActiveSidebarTab?.("Traces");
    setIsSecondarySidebarOpen(true);

    const next = new URLSearchParams(searchParams);
    next.delete(TRACES_DRAWER_INTENT);
    setSearchParams(next, { replace: true });
  }, [
    hasTracesDrawerIntent,
    searchParams,
    setActiveSidebarTab,
    setIsSecondarySidebarOpen,
    setSearchParams,
  ]);

  const handleReviewLogsPrimaryClick = () => {
    if (!workspaceId) {
      return;
    }

    const isActiveReviewLogsDrawer =
      isSecondarySidebarOpen && requestsTabActive && !hideSecondaryRequestsPanel;
    if (isActiveReviewLogsDrawer) {
      setShowReviewLogsDrawerOnAnalytics(false);
      setIsSecondarySidebarOpen(false);
      return;
    }

    setActiveSidebarTab?.("Requests");

    if (isRequestsAnalyticsSurface) {
      return;
    }

    setIsSecondarySidebarOpen(true);
  };

  const handleTracesPrimaryClick = () => {
    const isActiveTracesDrawer = isSecondarySidebarOpen && tracesTabActive;
    setActiveSidebarTab?.("Traces");
    setIsSecondarySidebarOpen(!isActiveTracesDrawer);
  };

  const observabilityDrawerOpen = isSecondarySidebarOpen && !hideSecondaryRequestsPanel;

  const reviewLogsActive =
    (observabilityDrawerOpen && requestsTabActive) ||
    (isRequestsAnalyticsSurface && requestsTabActive);
  const tracesDrawerOpen = observabilityDrawerOpen && tracesTabActive;
  const secondarySidebarRequestsOn = !tracesTabActive;
  const prevObservabilityTabRef = useRef<"Requests" | "Traces" | null>(null);

  useEffect(() => {
    const currentTab = requestsTabActive ? "Requests" : "Traces";
    if (
      observabilityDrawerOpen &&
      prevObservabilityTabRef.current !== null &&
      prevObservabilityTabRef.current !== currentTab
    ) {
      window.dispatchEvent(new Event(SCROLL_REVIEW_LOGS_DRAWER_TO_TOP_EVENT));
    }
    if (observabilityDrawerOpen) {
      prevObservabilityTabRef.current = currentTab;
    }
  }, [observabilityDrawerOpen, requestsTabActive]);

  const reviewLogsTooltip = "Requests";

  const handleSettingsBack = () => {
    skipAutoDetectRef.current = true;
    setSidebarView("navigation");
    if (location.pathname.includes("/settings")) {
      const returnUrl = sessionStorage.getItem(SESSION_STORAGE.SETTINGS_RETURN_URL);
      if (returnUrl && !returnUrl.includes("/settings")) {
        sessionStorage.removeItem(SESSION_STORAGE.SETTINGS_RETURN_URL);
        navigate(returnUrl);
      } else {
        navigate(-1);
      }
    }
  };

  return (
    <>
      <Sidebar
        className="relative z-[30] flex flex-col bg-white transition-all duration-300 ease-in-out"
        collapsible="icon"
        side="left"
      >
        <SidebarHeader className="relative h-auto flex-shrink-0 p-0">
          <div
            className={`flex flex-shrink-0 items-center border-b ${
              isCollapsed
                ? "h-auto flex-col justify-center gap-2 px-3 py-3"
                : "h-16 justify-between pl-5 pr-3"
            }`}
          >
            <button
              className={`group flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg transition-[background-color,box-shadow,opacity] duration-200 ease-out hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30 active:bg-gray-200 ${
                isCollapsed ? "opacity-100" : "pointer-events-none absolute opacity-0"
              }`}
              onClick={() => {
                document.querySelector<HTMLButtonElement>("[data-sidebar-trigger]")?.click();
              }}
              onMouseEnter={startLogoRotation}
              onMouseLeave={finishLogoRotation}
              tabIndex={isCollapsed ? 0 : -1}
            >
              <motion.img
                src={activeLogoCake}
                alt="PromptLayer"
                className="h-6 w-6 flex-shrink-0 origin-center object-contain"
                style={{ rotate: logoRotation }}
              />
            </button>
            <button
              className={`flex h-8 flex-shrink-0 items-center transition-opacity duration-200 hover:opacity-80 ${
                isCollapsed ? "pointer-events-none absolute opacity-0" : "opacity-100"
              }`}
              onClick={() =>
                navigate(
                  withPreservedWorkspaceSearch(`/workspace/${workspaceId}/home`, location.search),
                )
              }
              tabIndex={isCollapsed ? -1 : 0}
            >
              <img src={activeLogo} alt="PromptLayer" className="block h-6 w-auto" />
            </button>
            {!isCollapsed && (
              <BasicTooltip content="Collapse Sidebar">
                <Button
                  onClick={() => {
                    const sidebarContainer = document.querySelector(
                      '[data-slot="sidebar-container"]',
                    ) as HTMLElement;
                    if (sidebarContainer) {
                      const inlineWidth = sidebarContainer.style.width;
                      const currentWidth = inlineWidth
                        ? parseInt(inlineWidth.replace("px", ""), 10)
                        : sidebarContainer.offsetWidth;
                      if (currentWidth > 0) {
                        setSavedSidebarWidth(currentWidth);
                      }
                    }

                    document.querySelector<HTMLButtonElement>("[data-sidebar-trigger]")?.click();
                  }}
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-600 hover:text-gray-900"
                  aria-label="Collapse Sidebar"
                >
                  <PanelLeftClose className="h-4 w-4" />
                </Button>
              </BasicTooltip>
            )}
            {isCollapsed && (
              <BasicTooltip content="Expand Sidebar">
                <Button
                  onClick={() => {
                    document.querySelector<HTMLButtonElement>("[data-sidebar-trigger]")?.click();
                  }}
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-600 hover:text-gray-900"
                  aria-label="Expand Sidebar"
                >
                  <PanelLeft className="h-4 w-4" />
                </Button>
              </BasicTooltip>
            )}
          </div>

          <SidebarTrigger className="hidden" data-sidebar-trigger />
        </SidebarHeader>

        <SidebarContent className="relative overflow-hidden">
          <AnimatePresence initial={false}>
            {sidebarView === "navigation" && (
              <motion.div
                key="navigation"
                initial={{ x: "-100%", opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: "-100%", opacity: 0 }}
                transition={SLIDE_TRANSITION}
                className="absolute inset-0 flex h-full w-full flex-col"
              >
                <Navigation
                  onToggleObservability={handleReviewLogsPrimaryClick}
                  isObservabilityOpen={reviewLogsActive}
                  liveLogsTooltip={reviewLogsTooltip}
                  showObservabilityCaret={!isRequestsAnalyticsSurface}
                  onToggleTraces={handleTracesPrimaryClick}
                  isTracesOpen={tracesDrawerOpen}
                  showTracesCaret={!isTracesSurface}
                  onOpenSettings={() => {
                    setShowReviewLogsDrawerOnAnalytics(false);
                    setIsSecondarySidebarOpen(false);
                    sessionStorage.setItem(
                      SESSION_STORAGE.SETTINGS_RETURN_URL,
                      `${location.pathname}${location.search}`,
                    );
                    setSidebarView("settings");
                    navigate(`/workspace/${workspaceId}/settings/user`);
                  }}
                />
              </motion.div>
            )}
            {sidebarView === "settings" && (
              <motion.div
                key="settings"
                initial={{ x: "100%", opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: "100%", opacity: 0 }}
                transition={SLIDE_TRANSITION}
                className="absolute inset-0 flex h-full w-full flex-col"
              >
                <SettingsNavigation onBack={handleSettingsBack} />
              </motion.div>
            )}
          </AnimatePresence>
        </SidebarContent>

        <SidebarFooter className="mt-auto border-t border-gray-100 bg-gray-50/30">
          <UserSection isCollapsed={isCollapsed} />
        </SidebarFooter>

        <SidebarRail isSecondarySidebarOpen={observabilityDrawerOpen} />
      </Sidebar>

      <ObservabilitySecondarySidebarDrawer
        isVisible={isSecondarySidebarOpen && !hideSecondaryRequestsPanel}
        inputRef={inputRef}
        requestsOn={secondarySidebarRequestsOn}
        onRefresh={onRefresh}
        observabilityDrawerOpen={observabilityDrawerOpen}
        requestsTabActive={requestsTabActive}
      />
    </>
  );
};

export default observer(AppSidebar);
